import { UI, VStack } from '@genial/components';
import { BoxBorder } from '@genial/design-tokens';
import { Box, FlatList } from 'native-base';
import React, { useMemo } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { subscriptionCustomersAvailableQuery } from '../../../../store/subscription/selector';
import { SubscriptionDetailsTabsProps } from '../components/SubscriptionDetailsTabs';

import { SubscriptionCustomerEmptyList } from './components/SubscriptionCustomerEmptyList';
import { SubscriptionCustomerListHeader } from './components/SubscriptionCustomerListHeader';
import { SubscriptionCustomerListItem } from './components/SubscriptionCustomerListItem';
import { SubscriptionCustomerListItemLoading } from './components/SubscriptionCustomerListItemLoading';

const ItemSeparator = () => <Box height="stack.nano" />;

export interface SubscriptionCustomersListProps extends SubscriptionDetailsTabsProps {}

export const SubscriptionCustomersList: React.FC<SubscriptionCustomersListProps> = ({
  isMobile,
  params,
}) => {
  const marginTop = useMemo(() => (isMobile ? 'stack.nano' : 'stack.xxs'), [isMobile]);
  const padding = useMemo(() => (isMobile ? 0 : 'inset.md'), [isMobile]);
  const background = useMemo(() => (isMobile ? 'transparent' : 'neutral.white'), [isMobile]);

  const { data, loading, error, refresher } = useRecoilQuery(
    subscriptionCustomersAvailableQuery({ eventId: params.subscription.eventId }),
    { cachePolicy: 'no-cache' }
  );

  const renderContent = () => {
    if (loading) {
      return (
        <FlatList
          data={Array.from({ length: 8 })}
          showsVerticalScrollIndicator={false}
          ItemSeparatorComponent={ItemSeparator}
          keyExtractor={(_, index) => String(index)}
          renderItem={() => <SubscriptionCustomerListItemLoading />}
        />
      );
    }

    if (error) {
      return <UI.ErrorBoundary onRefresh={refresher} />;
    }

    return (
      <FlatList
        keyExtractor={(_, index) => `${index}`}
        windowSize={20}
        maxToRenderPerBatch={20}
        updateCellsBatchingPeriod={5}
        data={data?.data}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={1.5}
        ItemSeparatorComponent={ItemSeparator}
        renderItem={({ item }) => (
          <SubscriptionCustomerListItem
            subscriptionCustomer={item}
            subscriptionItem={params.subscription}
          />
        )}
        ListEmptyComponent={<SubscriptionCustomerEmptyList />}
      />
    );
  };

  const hasDataAndMoreThanOneItem = !!data?.data && (data?.data?.length || 0) > 1;

  return (
    <Box mt={marginTop} p={padding} borderRadius={BoxBorder.heavy} background={background}>
      <VStack space="stack.nano">
        <SubscriptionCustomerListHeader />
        <UI.Shadow
          level={isMobile || hasDataAndMoreThanOneItem ? 'none' : 1}
          borderRadius={BoxBorder.heavy}>
          {renderContent()}
        </UI.Shadow>
      </VStack>
    </Box>
  );
};
